import React, { useEffect, useRef } from 'react';

// The plugin-handoff waveform: a charcoal hairline wave breathing across the frame.
//   - mode="loading" — loops, with an accent-red dot riding the crest.
//   - mode="success" — the wave settles flat and fades while the dot blooms into
//     a spark ring and morphs into a small red check, then holds.

type Mode = 'loading' | 'success';

const INK = '42,40,36';
const ACCENT = '230,84,58';
const RATIOS = [0.18, 0.32, 0.46, 0.62, 0.8, 1.0];
const PHASE = 0.18;
const SETTLE = 0.9; // seconds until the success wave lies flat

const waveAmp = (t: number, time: number) => {
    const slow = Math.sin(t * Math.PI * 2 - time * 1.4);
    const fast = Math.sin(t * Math.PI * 3.7 - time * 2.1) * 0.35;
    return (slow + fast) * 4 * t * (1 - t);
};

const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);

export function HandoffWaveform({ mode }: { mode: Mode }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // mode is read inside the rAF loop; keep a ref so a mode flip doesn't need a
    // full effect teardown (it never flips in practice, but this keeps it safe).
    const modeRef = useRef<Mode>(mode);
    modeRef.current = mode;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let ctx = canvas.getContext('2d')!;
        let w = 0;
        let h = 0;

        const setup = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = (window.devicePixelRatio || 1) * 2;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx = canvas.getContext('2d')!;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            w = rect.width;
            h = rect.height;
        };
        setup();

        let doneTime = 0;
        let finished = false;

        // Draws one frame at `time` seconds since mount. Returns true once the
        // success sequence has fully resolved.
        const drawFrame = (time: number): boolean => {
            const cy = h / 2;
            const maxHalf = h * 0.36;
            const res = 200;

            ctx.fillStyle = '#F1F2ED';
            ctx.fillRect(0, 0, w, h);

            const success = modeRef.current === 'success';
            const settle = success ? easeOut(Math.min(1, time / SETTLE)) : 0;
            const calm = 1 - settle;
            const lineFade = success ? Math.max(0, Math.min(1, (time - SETTLE) / 0.5)) : 0;

            RATIOS.forEach((ratio, idx) => {
                const layerPhase = idx * PHASE;
                ctx.strokeStyle = `rgba(${INK},${(0.16 + ratio * 0.2) * (1 - lineFade)})`;
                ctx.lineWidth = 0.55;
                for (const dir of [-1, 1]) {
                    ctx.beginPath();
                    for (let i = 0; i <= res; i++) {
                        const t = i / res;
                        const x = t * w;
                        const amp = waveAmp(t, time + layerPhase) * ratio * maxHalf * (success ? calm : 1);
                        const y = cy + dir * amp;
                        if (i === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.stroke();
                }
            });

            if (!success) {
                // Accent dot sweeping left→right along the crest.
                const sT = (time / 2.4) % 1;
                const sY = cy + waveAmp(sT, time + 5 * PHASE) * maxHalf;
                ctx.fillStyle = `rgba(${ACCENT},0.92)`;
                ctx.beginPath();
                ctx.arc(sT * w, sY, 1.4, 0, Math.PI * 2);
                ctx.fill();
                return false;
            }

            // success: dot drifts, blooms, morphs into a check
            const wanderX = 0.5 + 0.45 * Math.sin(time * 1.6) * calm;
            const dotY = cy + waveAmp(wanderX, time + 5 * PHASE) * maxHalf * calm;

            const bt = (time - SETTLE * 0.7) / 0.55;
            if (bt > 0 && bt < 1) {
                for (let k = 0; k < 10; k++) {
                    const ang = (k / 10) * Math.PI * 2 + 0.4;
                    const rad = easeOut(bt) * 16;
                    ctx.fillStyle = `rgba(${ACCENT},${(1 - bt) * 0.8})`;
                    ctx.beginPath();
                    ctx.arc(w / 2 + Math.cos(ang) * rad, cy + Math.sin(ang) * rad * 0.55, 1.1, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            const mt = Math.max(0, Math.min(1, (time - SETTLE * 0.7 - 0.45) / 0.35));
            const cx = wanderX * w;
            if (mt < 1) {
                const r = (1.4 + settle * 1.0) * (1 - mt);
                if (r > 0.05) {
                    ctx.fillStyle = `rgba(${ACCENT},${0.92 * (1 - mt)})`;
                    ctx.beginPath();
                    ctx.arc(cx, dotY, r, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            if (mt > 0) {
                const p = easeOut(mt);
                ctx.strokeStyle = `rgba(${ACCENT},${0.92 * mt})`;
                ctx.lineWidth = 1.4;
                ctx.beginPath();
                const sx = w / 2 - 4.5;
                const sy = cy - 2;
                const l1 = Math.min(1, p * 2);
                ctx.moveTo(sx, sy);
                ctx.lineTo(sx + 3.5 * l1, sy + 3.5 * l1);
                if (p > 0.5) {
                    const l2 = (p - 0.5) * 2;
                    ctx.lineTo(sx + 3.5 + 6 * l2, sy + 3.5 - 8 * l2);
                }
                ctx.stroke();
            }

            return settle >= 1 && mt >= 1 && lineFade >= 1;
        };

        let raf = 0;
        let start = 0;

        const loop = (ms: number) => {
            if (!start) start = ms;
            const time = (ms - start) / 1000;
            if (drawFrame(time)) {
                finished = true;
                doneTime = time;
                return;
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        const onResize = () => {
            setup();
            if (finished) drawFrame(doneTime);
        };
        window.addEventListener('resize', onResize);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            className={`block w-[182px] ${mode === 'success' ? 'h-[48px]' : 'h-[68px]'}`}
        />
    );
}

export default HandoffWaveform;
